import { useMemo } from "react";
import { useSelector } from "react-redux";
import aa from "search-insights";

import { useAlgoliaActions } from "store/AlgoliaSlice";
import { SORT_ITEMS } from "enums";
import { jjLogger } from "utils/logUtils";
import UserPresenter from "presenters/UserPresenter";
import { ALGOLIA_FILTERS } from "enums/algoliaFilters";

import useUsers from "./useUsers";

const useAlgolia = () => {
  const { currentUser } = useUsers();
  const {
    addOneFilter,
    removeOneFilter,
    clearFilter,
    clearAllFilters,
    clearAllFiltersByPath,
    setIsClearAllClicked,
    setItemToRemove,
    setSortFilterSelected,
    setNumberOfPeopleFilters,
    setContentTypesFilters,
  } = useAlgoliaActions();
  const {
    castNumFilters,
    templateGroupTypeFilters,
    isClearAllClicked,
    itemToRemove,
    sortFilterSelected,
    numberOfPeopleFilters,
    contentTypesFilters,
  } = useSelector((state) => state.AlgoliaSlice);

  const isDefaultSort = sortFilterSelected?.label === SORT_ITEMS.bestMatch.label;

  const getUserToken = (sendTokenOnlyForPaidUser) => {
    let userToken;

    if (sendTokenOnlyForPaidUser) {
      if (UserPresenter.isPaid(currentUser)) userToken = UserPresenter.id(currentUser);
    } else {
      userToken = UserPresenter.id(currentUser);
      if (!userToken) {
        aa("getUserToken", null, (err, newUserToken) => {
          if (err) {
            jjLogger.log(`useAlgoliaEvents.js - getUserToken() - Error while generating newUserToken - ${err}`);
            return;
          }
          userToken = newUserToken;
        });
      }
    }
    return userToken;
  };

  const contentTypeFilterValues = useMemo(() => {
    return (path) => {
      return !contentTypesFilters[path]?.length ? [] : [contentTypesFilters[path].flatMap((item) => item.value)];
    };
  }, [contentTypesFilters]);

  const numberOfPeopleFilterValues = useMemo(() => {
    return (path) => {
      return !numberOfPeopleFilters[path]?.length
        ? []
        : [numberOfPeopleFilters[path].map((item) => `${ALGOLIA_FILTERS.castNumOptions}:${item}`)];
    };
  }, [numberOfPeopleFilters]);

  const filterValues = useMemo(() => {
    return (path) => {
      const contentTypeFiltersForPath = contentTypeFilterValues(path);
      const numberOfPeopleFiltersForPath = numberOfPeopleFilterValues(path);

      return [...contentTypeFiltersForPath, ...numberOfPeopleFiltersForPath];
    };
  }, [contentTypeFilterValues, numberOfPeopleFilterValues]);

  return {
    addOneFilter,
    removeOneFilter,
    clearFilter,
    clearAllFilters,
    clearAllFiltersByPath,
    castNumFilters,
    templateGroupTypeFilters,
    isClearAllClicked,
    setIsClearAllClicked,
    itemToRemove,
    setItemToRemove,
    setSortFilterSelected,
    sortFilterSelected,
    isDefaultSort,
    getUserToken,
    setNumberOfPeopleFilters,
    numberOfPeopleFilters,
    setContentTypesFilters,
    contentTypesFilters,
    filterValues,
  };
};

export default useAlgolia;
